import {
    Autocomplete,
    Chip,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import { useUnityIds } from '../hooks/useUnityIds';

export interface LocationInputProps {
    value: string;
    onChange: (value: string) => void;
}

export const LocationInput = ({ value, onChange }: LocationInputProps) => {
    const { data, isLoading } = useUnityIds();

    const selectedLocation =
        value && data
            ? data.locations.find((location) => location.id === value)
            : null;
    const isUnknown = selectedLocation === undefined;
    const isNonFree = selectedLocation ? !selectedLocation.isFree : false;
    const hasWarning = isUnknown || isNonFree;
    const warningMessage = isUnknown
        ? `'${value}' is an unknown location id.`
        : isNonFree
          ? `'${value}' is a non-free location.`
          : undefined;

    return (
        <Autocomplete
            value={value}
            onChange={(_, value) => {
                if (typeof value === 'string') {
                    onChange(value);
                } else if (value) {
                    onChange(value.id);
                } else {
                    onChange('');
                }
            }}
            freeSolo
            autoSelect
            loading={isLoading}
            options={
                data?.locations.toSorted((a, b) => {
                    return Number(b.isFree) - Number(a.isFree);
                }) ?? []
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Location"
                    color={hasWarning ? 'warning' : undefined}
                    error={hasWarning}
                    helperText={warningMessage}
                    slotProps={{
                        ...params.slotProps,
                        input: {
                            ...params.slotProps?.input,
                            type: 'search',
                        },
                    }}
                />
            )}
            renderOption={(props, option) => {
                return (
                    <li {...props} key={option.id}>
                        <Stack direction="row" spacing={1}>
                            {option.isFree ? (
                                <Chip color="success" label="Free" />
                            ) : (
                                <Chip color="warning" label="Paid" />
                            )}
                            <Typography>{option.id}</Typography>
                        </Stack>
                    </li>
                );
            }}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.id
            }
        />
    );
};
